import React, { useEffect, useState, useMemo } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import { InView } from 'react-intersection-observer'
import { isEmpty } from '@sainsburys-tech/boltui-utils'
import { Button, FilterPills, Image, ErrorBoundary } from '@sainsburys-tech/bolt'
import { withTheme } from 'styled-components'
import UGCQuickView from './UGCQuickView'
import UGCQuickViewStorystream from './UGCQuickViewStorystream'
import { GenerateCTABlock, CreateAlert } from './UGC.presentational'
import {
  createFilters,
  preventBodyScroll,
  getTagsFromUrl,
  persistTagsInUrl,
  formatTagLabel,
  formatImages,
  formatImagesStorystream
} from './ugc.helpers'
import { fetcher } from '../../../helpers/ComponentHelper'
import { onQuickViewSlidingPanelShut, onUGCLoaded, onUGCViewed, onUGCFilterInteraction } from './ugc.tracking'

import * as Styled from './ugc.styles'

const UGC = (props) => {
  const [UGCStatus, setStatus] = useState({
    isError: false,
    isLoading: true
  })

  const isMiniVariant = () => props.style === 'mini'
  const channel = props.consumingBrand || props.theme.channel
  const isSainsburysTheme = channel === 'sainsburys'
  const initialDisplayAmount = isSainsburysTheme ? 8 : 7

  const [imageData, setImageData] = useState({
    images: [],
    nextPage: isSainsburysTheme ? 1 : ''
  })

  const [selectedImage, setSelectedImage] = useState(null)
  const [selectedTags, setSelectedTags] = useState([])
  const [isViewed, setisViewed] = useState(false)
  const [loadMore, setLoadMore] = useState(true)
  const [viewMoreButtonClicked, setViewMoreButtonClicked] = useState(false)
  const [totalPages, setTotalPages] = useState(0)

  const toggleDrawer = () => {
    setSelectedImage(null)
    onQuickViewSlidingPanelShut(window)
    preventBodyScroll()
  }

  const trackComponentInView = () => {
    if (!isViewed) {
      onUGCViewed(window)
      setisViewed(true)
    }
  }

  const fetchUGCdata = async (after, tagsArray) => {
    const tagsString = tagsArray?.length > 0 ? tagsArray.toString() : ''
    const tagsFilter = tagsString || props.hashtag
    const apiUrl = isSainsburysTheme
      ? `https://d29qb9vav0xwuc.cloudfront.net/v4/sainsburys-recipes/60530e337f509811a661d264/search/live/.json?access_token=00f96100723124c628144a4c338e945f80e7f015&rpp=${
          props.limit
        }${after ? `&page=${after}` : ''}&tags=${tagsFilter}&all_media=true`
      : `/global-api/ugc-curalate?filter=${createFilters(tagsFilter)}&limit=${props.limit}${
          after ? `&after=${after}` : ''
        }`

    try {
      const { json, ok, status } = await fetcher(apiUrl)
      if (!ok) {
        throw new Error(`There has been an error with (${status})`)
      }
      if (isSainsburysTheme && json.blocks.length === 0) {
        throw new Error('There has been an error with the response')
      }
      if (isSainsburysTheme) {
        setTotalPages(json.total_pages)
      }

      const imageDataParameters = isSainsburysTheme
        ? {
            images: [...imageData.images, ...formatImagesStorystream(json.blocks)].filter(
              (image, index, self) => index === self.findIndex((img) => img.description === image.description)
            ),
            nextPage: after
          }
        : {
            images: [...imageData.images, ...formatImages(json.data.items)].filter(
              (image, index, self) => index === self.findIndex((img) => img.id === image.id)
            ),
            nextPage: json.paging.cursors.after || imageData.nextPage
          }
      return ReactDOM.unstable_batchedUpdates(() => {
        setImageData(imageDataParameters)
        setStatus({
          isError: false,
          isLoading: false
        })
        onUGCLoaded(props, window)
      })
    } catch (err) {
      console.error(err)
      return setStatus({
        isError: true,
        isLoading: false
      })
    }
  }

  const getRelatedImages = (image) => {
    if (!selectedImage) return []
    const { hashtag } = props
    const selectedImageTags = image.tags
    const displayTags = hashtag.split(',')
    const displayTagInSelectedImage = selectedImageTags.filter((tag) => displayTags.includes(tag))
    const relatedImages = imageData.images.filter((item) =>
      item.tags.some((tag) => displayTagInSelectedImage.includes(tag))
    )
    return relatedImages
  }

  const updateSelectedTags = (tag) => {
    const lowerCaseTag = tag.toLowerCase().trim()
    const tagArray = [...selectedTags]
    if (tagArray.includes(lowerCaseTag)) {
      tagArray.splice(tagArray.indexOf(lowerCaseTag), 1)
    } else {
      tagArray.push(lowerCaseTag)
    }
    setSelectedTags(tagArray)
    persistTagsInUrl(tagArray)
    fetchUGCdata(imageData.nextPage, tagArray)
    return onUGCFilterInteraction(props, selectedTags, lowerCaseTag, window)
  }

  const renderTags = (tags) =>
    tags.map((tag, index) => ({
      label: formatTagLabel(tag),
      reference: tag,
      active: selectedTags.includes(tag.toLowerCase().trim()),
      index
    }))

  const getImagesToRender = () => {
    const showAllAvailableImages = (isSainsburysTheme && viewMoreButtonClicked) || !isMiniVariant()

    if (isEmpty(selectedTags)) {
      return showAllAvailableImages ? imageData.images : imageData.images.slice(0, initialDisplayAmount)
    }
    const itemsToDisplay = imageData.images.reduce((imagesToRender, image) => {
      if (image.tags.some((tag) => selectedTags.indexOf(tag) >= 0)) {
        imagesToRender.push(image)
      }
      return imagesToRender
    }, [])

    return showAllAvailableImages ? itemsToDisplay : itemsToDisplay.slice(0, initialDisplayAmount)
  }

  const getImagesToRenderMemo = useMemo(() => getImagesToRender(), [imageData.images, selectedTags])

  const hideViewMore = () => (isSainsburysTheme ? getImagesToRenderMemo.length < 8 || !loadMore : !props.linkUrl)

  const isAlert = () => (!UGCStatus.isLoading && isEmpty(getImagesToRenderMemo)) || UGCStatus.isError

  const ctaClick = () => {
    const { tags, linkUrl } = props
    const hasPresetTag = linkUrl && linkUrl.indexOf('#') !== -1
    const linkSelectedTags = tags.length > 1 ? `${hasPresetTag ? ',' : '#'}${selectedTags}` : ''
    const stripTrailingComma = (string) => string.replace(/(^,)/g, '')
    const ctaUrl = `${linkUrl}${stripTrailingComma(linkSelectedTags)}`

    window.location.href = ctaUrl
  }
  const ctaClickStorystream = () => {
    const newLoadMore = imageData.nextPage + 1 < totalPages
    setLoadMore(newLoadMore)
    getImagesToRender()
    setViewMoreButtonClicked(true)
    fetchUGCdata(imageData.nextPage + 1, selectedTags)
  }

  const renderImages = () => {
    const imagesToRender = [...getImagesToRenderMemo]
    return imagesToRender.map((item, index) => (
      <Styled.ImageItem
        data-test={`ugc-image-container-${index}`}
        key={`${item.url}-${index}`}
        onClick={() => {
          setSelectedImage(item)
          preventBodyScroll()
        }}
      >
        <Image src={item.url} alt={`@${item && item.author}`} data-index={JSON.stringify(item.productIds)} lazy />
      </Styled.ImageItem>
    ))
  }
  const renderImagesMemo = useMemo(() => renderImages(), [imageData.images, selectedTags])

  useEffect(() => {
    setSelectedTags(getTagsFromUrl())
  }, [])

  useEffect(() => {
    if (imageData.images.length === 0) {
      fetchUGCdata(imageData.nextPage, selectedTags)
    }
  }, [imageData.images, imageData.nextPage, selectedTags])

  return (
    <InView onChange={(inView) => inView && trackComponentInView()}>
      {props.tags.length > 1 && (
        <Styled.TagContainer>
          <Styled.FilterTags>
            <FilterPills
              pills={renderTags(props.tags)}
              onActivate={(tag) => updateSelectedTags(tag.reference)}
              onDeactivate={(tag) => updateSelectedTags(tag.reference)}
              data-test='social-content-filters'
            />
          </Styled.FilterTags>
        </Styled.TagContainer>
      )}
      {isAlert() ? (
        CreateAlert(UGCStatus, imageData, channel)
      ) : (
        <>
          <Styled.ImageContainer data-test='ugc-images-container'>
            {!isSainsburysTheme && <Styled.ImageItem>{GenerateCTABlock()}</Styled.ImageItem>}
            {renderImagesMemo}
          </Styled.ImageContainer>
          {!hideViewMore() && (
            <Styled.ViewMore>
              <Button
                kind='secondary'
                onClick={isSainsburysTheme ? ctaClickStorystream : ctaClick}
                title={props.linkTitle}
                data-test='m024-view-more'
              >
                View more
              </Button>
            </Styled.ViewMore>
          )}
          <ErrorBoundary>
            {!isSainsburysTheme ? (
              <UGCQuickView
                displayImage={selectedImage}
                galleryImages={getRelatedImages(selectedImage)}
                updateDrawerInformation={(image) => setSelectedImage(image)}
                toggleCallback={toggleDrawer}
                productTab={props.productTab}
              />
            ) : (
              <UGCQuickViewStorystream displayImage={selectedImage} toggleCallback={toggleDrawer} />
            )}
          </ErrorBoundary>
        </>
      )}
    </InView>
  )
}

UGC.propTypes = {
  hashtag: PropTypes.string.isRequired,
  limit: PropTypes.number.isRequired,
  linkTitle: PropTypes.string,
  linkUrl: PropTypes.string,
  style: PropTypes.oneOf(['full', 'mini']),
  tags: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  productTab: PropTypes.string
}

UGC.defaultProps = {
  linkTitle: 'View more',
  style: 'full',
  limit: 50,
  productTab: 'Shop these products'
}

export default withTheme(UGC)
